import React from 'react';
import PageBanner from '../../components/PageBanner';
import SEO from '../../components/SEO';
import SectionSidebar from '../../components/SectionSidebar';
import { Container, Typography, Box, Grid, Chip } from '@mui/material';
import CelebrationIcon from '@mui/icons-material/Celebration';
import TempleHinduIcon from '@mui/icons-material/TempleHindu';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import WbIncandescentIcon from '@mui/icons-material/WbIncandescent';
import NightlightRoundIcon from '@mui/icons-material/NightlightRound';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const GOLD = '#d4af37';

export default function FestivalPoojas() {
  const kottayilObservances = [
    { name: 'Ganapathi Homam', icon: LocalFireDepartmentIcon },
    { name: 'Special Poojas', icon: AutoAwesomeIcon },
    { name: 'Chathussatha Pooja', icon: TempleHinduIcon },
    { name: 'Purification Rituals', icon: AutoAwesomeIcon },
    { name: 'Homas', icon: LocalFireDepartmentIcon },
    { name: 'Sree Bhootha Bali', icon: TempleHinduIcon },
    { name: 'Deeparadhana', icon: WbIncandescentIcon },
    { name: 'Festival Ritual Sequence', icon: CelebrationIcon },
  ];

  const kovilakamObservances = [
    { name: 'Ganapathi Homam', icon: LocalFireDepartmentIcon },
    { name: 'Usha Pooja', icon: WbIncandescentIcon },
    { name: 'Madhyahna Pooja', icon: TempleHinduIcon },
    { name: 'Deeparadhana', icon: WbIncandescentIcon },
    { name: 'Athazha Pooja', icon: NightlightRoundIcon },
  ];

  const notes = [
    'Temple timings are extended during festivals',
    'Special darshan arrangements for devotees',
    'Prasadam distribution after major rituals',
    'Parking and accommodation facilities available',
  ];

  return (
    <Box sx={{ bgcolor: '#fafaf8', minHeight: '100vh' }}>
      <SEO title="Festival Poojas — Kottayil Temple | ഉത്സവ പൂജകൾ" description="Festival pooja schedules and special rituals during ulsavam at Kottayil Temple (Kotta-Kovilakam Kshethram), Muchukunnu, Kerala." path="/poojas/festival" keywords="festival poojas, Kottayil temple festival, ulsavam poojas, festival rituals, special festival schedule, ഉത്സവ പൂജ, കൊട്ടയിൽ ക്ഷേത്രം ഉത്സവം, temple festival Kerala" />
      <PageBanner title="Festival Day Details" />
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} lg={9}>

            {/* ══════ VIBRANT HERO ══════ */}
            <Box sx={{
              borderRadius: '24px', overflow: 'hidden', mb: 3.5,
              background: 'linear-gradient(135deg, #881337 0%, #be123c 50%, #e11d48 100%)',
              position: 'relative',
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.07, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
              <Box sx={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', bottom: -40, left: -40, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }} />

              <Box sx={{ px: { xs: 3, sm: 4 }, pt: { xs: 3.5, sm: 4 }, pb: { xs: 4, sm: 5 }, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2.5 }}>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '20px',
                    bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                    border: '2px solid rgba(255,255,255,0.25)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <CelebrationIcon sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Chip label="Temple Ulsavam" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700, fontSize: '0.7rem', mb: 1, height: 24, backdropFilter: 'blur(8px)' }} />
                    <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.5rem', sm: '1.8rem' }, color: '#fff', lineHeight: 1.2 }}>
                      Ulsavam Day Observances At Both Temples
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.8)', lineHeight: 1.8, maxWidth: 600 }}>
                  This section describes the important rituals, poojas, and observance sequence conducted
                  on ulsavam day at both Kottayil Temple and Kovilakam Temple.
                </Typography>
              </Box>
            </Box>

            {/* ══════ TEMPLE OBSERVANCES ══════ */}
            <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
              {[
                {
                  title: 'Kottayil Temple - Ulsavam Day Details',
                  subtitle: 'Poojas, bali, homams and ritual observances',
                  gradient: 'linear-gradient(135deg, #7f1d1d, #b91c1c)',
                  items: kottayilObservances,
                },
                {
                  title: 'Kovilakam Temple - Ulsavam Day Details',
                  subtitle: 'Day-sequence poojas and evening observances',
                  gradient: 'linear-gradient(135deg, #1e3a8a, #2563eb)',
                  items: kovilakamObservances,
                },
              ].map((section) => (
                <Grid item xs={12} md={6} key={section.title}>
                  <Box sx={{
                    bgcolor: '#fff', borderRadius: '24px',
                    border: '1px solid rgba(0,0,0,0.06)',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
                    overflow: 'hidden',
                    height: '100%',
                  }}>
                    <Box sx={{ background: section.gradient, px: 3, py: 2.2 }}>
                      <Typography sx={{ fontWeight: 900, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#fff', mb: 0.4 }}>
                        {section.title}
                      </Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.82)', lineHeight: 1.5 }}>
                        {section.subtitle}
                      </Typography>
                    </Box>

                    <Box sx={{ px: 2.5, py: 2.3 }}>
                      <Grid container spacing={1.4}>
                        {section.items.map((item) => (
                          <Grid item xs={12} key={item.name}>
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.3,
                              px: 1.4,
                              py: 1.1,
                              borderRadius: '12px',
                              bgcolor: 'rgba(15, 23, 42, 0.03)',
                              border: '1px solid rgba(15, 23, 42, 0.06)',
                            }}>
                              <Box sx={{
                                width: 30,
                                height: 30,
                                borderRadius: '9px',
                                bgcolor: `${GOLD}22`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                              }}>
                                <item.icon sx={{ fontSize: 17, color: '#8a6c12' }} />
                              </Box>
                              <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#1e293b' }}>
                                {item.name}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* ══════ DARK SLATE FOOTER ══════ */}
            <Box sx={{
              borderRadius: '24px', overflow: 'hidden', position: 'relative',
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
              <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }} />
              <Box sx={{ position: 'absolute', bottom: -30, right: -30, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(212,175,55,0.08) 0%, transparent 70%)' }} />

              <Box sx={{ px: { xs: 3, sm: 4 }, py: 3.5, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Box sx={{
                    width: 40, height: 40, borderRadius: '12px',
                    background: `linear-gradient(135deg, ${GOLD}, #f59e0b)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 16px rgba(212,175,55,0.3)',
                  }}>
                    <InfoOutlinedIcon sx={{ fontSize: 20, color: '#fff' }} />
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#fff' }}>
                    Festival Day Notes
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  {notes.map((note, idx) => (
                    <Grid item xs={12} sm={6} key={idx}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <CheckCircleOutlineIcon sx={{ fontSize: 16, color: GOLD, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.6, fontWeight: 500 }}>
                          {note}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>

          </Grid>

          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="poojas" activePath="/poojas/festival" />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
